'use client';

import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { getHost } from '@repo/host/host';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectIsMiniMapMode, selectIsMobileViewport } from '@repo/store/maps/slices/clientContext';
import { selectMapData, selectSelectedHeatmapEvent } from '@repo/store/maps/slices/mapData';
import { HostMode, HostType } from '@repo/types/host';
import type { MapInitializationData } from '@repo/types/maps/map-types';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CompassRouteLayer } from '../../compass/compass-route-layer';
import { useCompassCta } from '../../compass/use-compass-cta';
import { useMapCompassEnabled } from '../../compass/use-map-compass-enabled';
import { CompassCtaCard, LiveLocationCtaCard, TeleportCtaCard } from '../../components/map-feature-cta-card';
import { BulkMoveMapLocationsControls } from '../../creator-tools/components/bulk-move-map-locations';
import { BulkMoveSelectedPinsControls } from '../../creator-tools/components/bulk-move-selected-pins';
import { BulkSelectMapPinsControls } from '../../creator-tools/components/bulk-select-map-pins';
import { CreatorToolsPanel } from '../../creator-tools/creator-tools-panel';
import { LiveLocationCtaMarker } from '../../live-location-cta/live-location-cta-marker';
import { useLiveLocationCta } from '../../live-location-cta/use-live-location-cta';
import { HeatmapEventInfo } from '../../location-info/heatmap-event-info';
import { useLocationInfo } from '../../location-info/hooks/useLocationInfo';
import { useStaticNearbyPins } from '../../location-info/hooks/useStaticNearbyPins';
import { LocationInfo } from '../../location-info/location-info';
import { MapControls } from '../../map-controls/map-controls';
import { SessionTimeline } from '../../map-controls/session-timeline';
import { MapSideBar } from '../../map-sidebar/map-side-bar';
import { MiniMapCtaCard } from '../../mini-map-cta/mini-map-cta-card';
import { useMiniMapCta } from '../../mini-map-cta/use-mini-map-cta';
import { DEFAULT_VIEW_PEEK_HEIGHT, MAP_CONTROLS_DRAWER_GAP, MapMobilePane } from '../../mobile-pane/map-mobile-pane';
import { useTeleportCta } from '../../teleport/use-teleport-cta';
import { MapToolbar } from '../../toolbar/map-toolbar';
import { isStandaloneWebMapHost } from '../../utils/is-standalone-web-map-host';
import { getIsConfiguredMiniMap, getIsDedicatedMiniMap, getIsSessionMiniMap } from '../../utils/mini-map-mode';
import { HeatmapLab } from '../dev/heatmap-lab';
import { useMapPinOperations } from '../hooks/useMapPinOperations';
import { useNearbyPinNotification } from '../hooks/useNearbyPinNotification';
import { useSessionHistory } from '../hooks/useSessionHistory';
import { ValuePropDownloadBar } from '../value-prop-download-bar';
import { Watermark } from '../watermark';
export const GameMapUI = ({
  providedMapData,
  sidebarAd
}: {
  providedMapData: MapInitializationData;
  sidebarAd?: React.ReactNode;
}) => {
  const isMobileViewport = useAppSelector(selectIsMobileViewport);
  const isMiniMapMode = useAppSelector(selectIsMiniMapMode);
  const hostType = useHostType();
  const hostMode = useHostMode();
  const {
    timelineVisible,
    currentSession,
    setCurrentSession
  } = useSessionHistory();
  const {
    selectMarkerInMap,
    togglePinFound,
    clearSelectedMarkerInMap
  } = useMapPinOperations();
  const {
    selectedLocation
  } = useLocationInfo();
  const mapData = useAppSelector(selectMapData);
  const selectedHeatmapEvent = useAppSelector(selectSelectedHeatmapEvent);
  const nearbyPins = useStaticNearbyPins(selectedLocation, mapData);
  useNearbyPinNotification();

  // Defer embed detection to avoid hydration mismatch (mirrors watermark.tsx).
  const [isEmbed, setIsEmbed] = useState(false);
  useEffect(() => {
    setIsEmbed(hostMode === HostMode.EMBED && hostType === HostType.WEB);
  }, [hostMode, hostType]);
  const effectiveMiniMapMode = isMiniMapMode || getIsConfiguredMiniMap(providedMapData.mapConfig);
  const isSessionMiniMap = getIsSessionMiniMap(providedMapData.mapConfig, effectiveMiniMapMode);
  const isDedicatedMiniMap = getIsDedicatedMiniMap(providedMapData.mapConfig);
  const liveLocationCta = useLiveLocationCta({
    clearSelectedMarkerInMap,
    isMiniMapMode: effectiveMiniMapMode,
    isMobileViewport,
    isSessionMiniMap,
    selectedLocationId: selectedLocation?.properties.externalId
  });
  const miniMapCta = useMiniMapCta({
    isMiniMapMode: effectiveMiniMapMode,
    isMobileViewport
  });
  useEffect(() => {
    const disposable = getHost().onSelectPin((externalId: string) => {
      if (isDedicatedMiniMap || isSessionMiniMap) {
        return;
      }
      selectMarkerInMap(externalId, {
        flyTo: true,
        selectSource: 'notification_nearby'
      });
    });
    return () => disposable.dispose();
  }, [isDedicatedMiniMap, isSessionMiniMap, selectMarkerInMap]);
  const togglePinFoundRef = useRef(togglePinFound);
  togglePinFoundRef.current = togglePinFound;
  useEffect(() => {
    const disposable_0 = getHost().onTogglePinFound((externalId_0: string) => {
      togglePinFoundRef.current(externalId_0, 'overlay-notification', false);
    });
    return () => disposable_0.dispose();
  }, []);
  const handleTimelineSharingChange = useCallback((_sessionUuid: string, isShared: boolean) => {
    if (currentSession) {
      setCurrentSession({
        ...currentSession,
        isShared
      });
    }
  }, [currentSession, setCurrentSession]);

  // In embed mode, always use desktop layout so LocationInfo (pin details) is visible.
  // MapMobilePane returns null in embed, so mobile layout would hide pin info entirely.
  const isMobileWeb = !isEmbed && isMobileViewport && (hostType === HostType.WEB || hostType === HostType.REMOTE);
  const mapCompassEnabled = useMapCompassEnabled();
  const compassCta = useCompassCta({
    enabled: mapCompassEnabled,
    isMobileWeb,
    selectedLocationId: selectedLocation?.properties.externalId
  });
  const teleportCta = useTeleportCta({
    selectedLocationId: selectedLocation?.properties.externalId
  });
  const showMobilePane = isMobileWeb && !effectiveMiniMapMode;
  const hideWatermark = hostType === HostType.OVERLAY;
  const showValuePropDownloadBar = !effectiveMiniMapMode && isStandaloneWebMapHost(hostType, hostMode);
  return <>
            {!hideWatermark && <Watermark />}
            {mapCompassEnabled && <CompassRouteLayer />}
            {/* Heatmap tuning panel; renders null unless `?heatlab=1`. */}
            <HeatmapLab />
            {showMobilePane ? <>
                    <MapToolbar providedMapData={providedMapData} />
                    <MapMobilePane teleportCta={teleportCta} />
                    <div className={css({
        position: 'absolute',
        right: '12',
        zIndex: '[2]',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8'
      })} style={{
        bottom: `${DEFAULT_VIEW_PEEK_HEIGHT + MAP_CONTROLS_DRAWER_GAP}px`
      }}>
                        <MapControls hideLayerControl />
                    </div>
                    {currentSession && <SessionTimeline session={currentSession} visible={timelineVisible} position="top" onSharingChange={handleTimelineSharingChange} />}
                </> : <>
                    {!effectiveMiniMapMode && !isEmbed && <MapToolbar providedMapData={providedMapData} />}
                    {!effectiveMiniMapMode && <MapSideBar providedMapData={providedMapData} sidebarAd={sidebarAd} />}
                    {showValuePropDownloadBar && <ValuePropDownloadBar />}
                    <CreatorToolsPanel />
                    <BulkMoveMapLocationsControls />
                    <BulkSelectMapPinsControls />
                    <BulkMoveSelectedPinsControls />
                    {liveLocationCta.showMarker && <LiveLocationCtaMarker onOpen={liveLocationCta.handleMarkerOpen} onHover={liveLocationCta.handleMarkerHover} />}

                    <FlexRow gap={miniMapCta.isOpen || teleportCta.isOpen || compassCta.isOpen || liveLocationCta.isOpen && liveLocationCta.isEligible ? '16' : '12'} className={css.raw({
        position: 'absolute',
        bottom: '16',
        right: '16',
        zIndex: 1,
        alignItems: 'end'
      })}>
                        {!effectiveMiniMapMode && (miniMapCta.isOpen && miniMapCta.variant ? <MiniMapCtaCard isLaunchSuccessful={miniMapCta.isLaunchSuccessful} isLaunching={miniMapCta.isLaunching} titleName={miniMapCta.titleName} trainerConnected={miniMapCta.trainerConnected} variant={miniMapCta.variant} onClose={miniMapCta.close} onLaunch={miniMapCta.launch} /> : teleportCta.isOpen ? <TeleportCtaCard onClose={teleportCta.close} onDownloadClick={teleportCta.handleDownloadClick} /> : compassCta.isOpen ? <CompassCtaCard onClose={compassCta.close} onDownloadClick={compassCta.handleDownloadClick} /> : liveLocationCta.isOpen && liveLocationCta.isEligible ? <LiveLocationCtaCard onClose={liveLocationCta.close} onDownloadClick={liveLocationCta.handleDownloadClick} /> : selectedHeatmapEvent ? <HeatmapEventInfo /> : <LocationInfo nearbyPins={nearbyPins} />)}
                        {!isEmbed && <MapControls liveLocationCta={liveLocationCta.control} />}
                    </FlexRow>

                    {currentSession && <SessionTimeline session={currentSession} visible={timelineVisible} onSharingChange={handleTimelineSharingChange} />}
                </>}
        </>;
};